'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, api, session, type Me } from '@/lib/api/client';

/**
 * Admin session, against the Lejo API.
 *
 * Admin accounts are ordinary users carrying the `admin` role. The role is
 * granted server-side only -- there is no endpoint that grants it, which is
 * what closed the privilege-escalation hole in the previous system. So this
 * context can trust the roles it receives but must still check them: signing
 * in successfully does not make someone an administrator.
 */

/**
 * The shape the existing chrome (sidebar, topbar) already reads. Derived from
 * the API's `/me` rather than a separate profile table, so there is one source
 * of truth for who is signed in.
 */
export interface AdminProfile {
  id: string;
  full_name: string;
  role: string;
  avatar_initials: string;
}

interface AuthContextValue {
  user: Me | null;
  profile: AdminProfile | null;
  loading: boolean;
  isAdmin: boolean;
  /** Sends the code and returns whether the number is known. */
  signIn: (phone: string, code: string) => Promise<Me>;
  signInWithPassword: (email: string, password: string) => Promise<Me>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export class NotAnAdminError extends Error {
  constructor() {
    super('This account does not have administrator access.');
    this.name = 'NotAnAdminError';
  }
}

/** "Amrit Singh" -> "AS"; a phone number falls back to its last two digits. */
function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return name.replace(/[^a-zA-Z0-9]/g, '').slice(-2).toUpperCase() || '?';
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore on load: the access token lives in memory and is gone after a
  // refresh of the page, so it is rebuilt from the stored refresh token.
  useEffect(() => {
    let cancelled = false;
    api.restore()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (phone: string, code: string): Promise<Me> => {
    const result = await api.verify(`${phone}:${code}`);
    session.set(result);

    const me = await api.get<Me>('/me');
    if (!me.roles.includes('admin') && !me.roles.includes('support')) {
      // Do not leave a usable session lying around for an account that has no
      // business here.
      session.clear();
      throw new NotAnAdminError();
    }

    setUser(me);
    return me;
  }, []);

  /**
   * The same checks as a phone sign-in: the server says who they are, this
   * says whether they belong here, and a session that fails the second is
   * cleared rather than left lying around.
   */
  const signInWithPassword = useCallback(
    async (email: string, password: string): Promise<Me> => {
      const result = await api.signInWithPassword(email, password);
      session.set(result);

      const me = await api.get<Me>('/me');
      if (!me.roles.includes('admin') && !me.roles.includes('support')) {
        session.clear();
        throw new NotAnAdminError();
      }

      setUser(me);
      return me;
    }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // A failed logout call must not trap someone in the app. The local
      // session is cleared either way.
      if (!(err instanceof ApiError)) throw err;
    }
    session.clear();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<Me>('/me'));
    } catch {
      session.clear();
      setUser(null);
    }
  }, []);

  const profile = useMemo<AdminProfile | null>(() => {
    if (!user) return null;
    const name = user.displayName?.trim() || user.phone;
    return {
      id: user.id,
      full_name: name,
      role: user.roles.includes('admin') ? 'Administrator' : 'Support',
      avatar_initials: initials(name),
    };
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      isAdmin: Boolean(user?.roles.includes('admin')),
      signIn,
      signInWithPassword,
      signOut,
      refresh,
    }),
    [user, profile, loading, signIn, signInWithPassword, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
